import { MouseEvent, useCallback, useEffect, useRef, useState } from "react"

import { Button } from "@atoms/index"

import AlertBanner from "@molecules/AlertBanner/AlertBanner"
import CheckboxCard from "@molecules/CheckboxCard/CheckboxCard"
import Dialog from "@molecules/Dialog/Dialog"

import styles from "./BillVendorCreditLinePickerDialog.module.scss"
import {
    BillVendorCreditLinePickerDialogProps,
    BillVendorCreditLinePickerLineItem,
} from "./BillVendorCreditLinePickerDialog.types"

// Read what the vendor credits holding a bill line took from it. Quantities are compared in whole thousandths and
// amounts in whole cents so the decimal strings add up without float drift. A line is fully consumed once either its
// quantity or its amount is used up, since the server refuses more credit at that point.
function getLineItemCreditState(lineItem: BillVendorCreditLinePickerLineItem) {
    const lineQuantity = Math.round(parseFloat(lineItem.quantity) * 1000)
    const lineAmount = Math.round(parseFloat(lineItem.quantity) * parseFloat(lineItem.price) * 100)

    let creditedQuantity = 0
    let creditedAmount = 0
    lineItem.vendor_credits.forEach((vendorCredit) => {
        creditedQuantity += Math.round(parseFloat(vendorCredit.quantity) * 1000)
        creditedAmount += Math.round(parseFloat(vendorCredit.amount) * 100)
    })

    const creditDescriptions = lineItem.vendor_credits.map(
        (vendorCredit) =>
            `${vendorCredit.label} (${parseFloat(vendorCredit.quantity)} of ${parseFloat(lineItem.quantity)})`,
    )

    return {
        isUntouched: lineItem.vendor_credits.length === 0,
        // The server refuses more credit once either the quantity or the money is used up, so either one closes the line
        isFullyConsumed: creditedQuantity >= lineQuantity || creditedAmount >= lineAmount,
        supportText: creditDescriptions.length === 0 ? "" : `On ${creditDescriptions.join(", ")}`,
    }
}

export default function BillVendorCreditLinePickerDialog(props: BillVendorCreditLinePickerDialogProps) {
    const { isOpen, onOpenChange, lineItems, onCreateVendorCredit } = props

    const lineItemsWithCreditState = lineItems.map((lineItem) => ({
        lineItem,
        creditState: getLineItemCreditState(lineItem),
    }))

    // Only the lines no credit touched start checked; a partly credited line stays unchecked but choosable
    const defaultSelectedLineItemIDs = lineItemsWithCreditState
        .filter(({ creditState }) => creditState.isUntouched)
        .map(({ lineItem }) => lineItem.id)
    const isFullyCredited = lineItemsWithCreditState.every(({ creditState }) => creditState.isFullyConsumed)

    const [selectedLineItemIDs, setSelectedLineItemIDs] = useState(defaultSelectedLineItemIDs)
    const [hasSelectionError, setHasSelectionError] = useState(false)

    // Store onOpenChange in a ref so the callback identity stays stable
    // across renders. The Popover's useEffect depends on onOpenChange,
    // and an unstable reference causes an infinite re-render loop.
    const onOpenChangeRef = useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange

    const stableOnOpenChange = useCallback((isNowOpen: boolean) => {
        onOpenChangeRef.current(isNowOpen)
    }, [])

    // Re-sync the selection each time the dialog opens so lines credited (or
    // freed) since the last open are reflected.
    useEffect(() => {
        if (isOpen) {
            setSelectedLineItemIDs(defaultSelectedLineItemIDs)
            setHasSelectionError(false)
        }
    }, [isOpen])

    const handleToggle = (lineItemID: number) => {
        setSelectedLineItemIDs((prevSelected) =>
            prevSelected.includes(lineItemID)
                ? prevSelected.filter((selectedID) => selectedID !== lineItemID)
                : [...prevSelected, lineItemID],
        )
    }

    const handleCreateVendorCredit = (event: MouseEvent<HTMLButtonElement>) => {
        if (selectedLineItemIDs.length === 0) {
            setHasSelectionError(true)
        } else {
            onCreateVendorCredit(selectedLineItemIDs, event)
        }
    }

    return (
        <Dialog.Root mode="dialog" variant="default" isOpen={isOpen} onOpenChange={stableOnOpenChange}>
            <Dialog.Content>
                <Dialog.Header.Root>
                    <Dialog.Header.Title title="Create Vendor Credit" />
                    <Dialog.Header.CloseButton />
                </Dialog.Header.Root>
                <Dialog.Body>
                    <div className={styles.body}>
                        {isFullyCredited ? (
                            <AlertBanner
                                type="card"
                                status="info"
                                message="Every line on this bill is already fully credited on a vendor credit."
                            />
                        ) : (
                            <p>Select which lines you&apos;d like to credit:</p>
                        )}
                        {lineItemsWithCreditState.map(({ lineItem, creditState }) => (
                            <CheckboxCard
                                key={lineItem.id}
                                checked={selectedLineItemIDs.includes(lineItem.id)}
                                onCheckedChange={() => handleToggle(lineItem.id)}
                                label={`${lineItem.description} (Qty: ${parseFloat(lineItem.quantity)})`}
                                showLabel={true}
                                supportText={creditState.supportText}
                                showSupportText={!!creditState.supportText}
                                isControlled={false}
                                isDisabled={creditState.isFullyConsumed}
                            />
                        ))}
                        {hasSelectionError && (
                            <span className={styles.errorMessage}>Please select at least one line to credit.</span>
                        )}
                    </div>
                </Dialog.Body>
                <Dialog.Footer.Root>
                    <Button
                        variant="solid"
                        size="lg"
                        onClick={handleCreateVendorCredit}
                        isFullWidth={true}
                        isDisabled={isFullyCredited}
                    >
                        Create Credit
                    </Button>
                </Dialog.Footer.Root>
            </Dialog.Content>
        </Dialog.Root>
    )
}
